/**
 * OverviewTab — deal-at-a-glance for the Property Command Center.
 *
 * No new endpoints. Pulls property + latest Deal Score + AI next-actions
 * from what's already on the record, and surfaces them as a two-column
 * hero that gives the operator "should I keep working on this?" in one
 * glance — same job ReSimpli's Lead Detail hero does.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Loader2, Sparkles, TrendingUp, Target, Wrench, DollarSign,
  Calendar, ArrowRight, AlertCircle,
} from "lucide-react";

const STAGE_META = {
  lead:           { label: "Lead",           color: "#64748B" },
  qualifying:     { label: "Qualifying",     color: "#3B82F6" },
  under_contract: { label: "Under Contract", color: "#8B5CF6" },
  marketing:      { label: "Marketing",      color: "#F59E0B" },
  pending_close:  { label: "Pending Close",  color: "#EA580C" },
  closed:         { label: "Closed",         color: "#10B981" },
  dead:           { label: "Dead",           color: "#EF4444" },
};

const money = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

export default function OverviewTab({ property }) {
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/v3/properties/${property.id}/ai-recommendations/current`,
        );
        if (!cancel) setRecs(data);
      } catch { /* no recs yet — fine */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [property.id]);

  const stage = STAGE_META[property.stage] || STAGE_META.lead;
  const kpis = useMemo(() => {
    const arv = Number(property.estimated_arv) || null;
    const rep = Number(property.estimated_repairs) || null;
    const ask = Number(property.asking_price) || null;
    const mao = arv != null && rep != null ? Math.max(0, arv * 0.70 - rep) : null;
    const margin = mao != null && ask != null ? mao - ask : null;
    return [
      { label: "ARV",      value: money(arv), icon: TrendingUp, color: "#3B82F6" },
      { label: "Asking",   value: money(ask), icon: DollarSign, color: "#EA580C" },
      { label: "Repairs",  value: money(rep), icon: Wrench,     color: "#F59E0B" },
      { label: "MAO 70%",  value: money(mao), icon: Target,     color: "#8B5CF6" },
      { label: "Margin",   value: money(margin), icon: TrendingUp,
        color: (margin ?? 0) >= 0 ? "#10B981" : "#DC2626" },
    ];
  }, [property]);

  return (
    <div className="space-y-5" data-testid="tab-panel-overview">
      {/* Stage + last-touched banner */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-widest ring-1"
          style={{ color: stage.color, backgroundColor: `${stage.color}12`, borderColor: `${stage.color}30` }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stage.color }} />
          {stage.label}
        </span>
        <div className="text-[12px] text-slate-500 inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Updated {property.updated_at?.slice(0, 10) || "—"}
        </div>
        {property.deal_score != null && (
          <div className="ml-auto text-[12px] text-slate-600">
            Deal Score <span className="font-bold text-slate-900 tabular-nums">{property.deal_score}</span>/100
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpis.map(({ label, value, icon: Icon, color }) => (
          <div key={label}
               data-testid={`overview-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
               className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-widest font-black text-slate-500">
              <Icon className="w-3 h-3" style={{ color }} /> {label}
            </div>
            <div className="mt-1 text-lg font-bold tabular-nums text-slate-900">{value}</div>
          </div>
        ))}
      </div>

      {/* Next actions from AI Recommendations */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-black text-amber-700">
            <Sparkles className="w-3.5 h-3.5" /> Next Actions
          </div>
          <Link
            to={`/v3/intel/ai-recommendations?property_id=${property.id}`}
            className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1"
          >
            Full recommendations <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {loading ? (
          <div className="p-6 text-slate-500 text-sm inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading AI recommendations…
          </div>
        ) : !recs?.recommended_actions?.length ? (
          <div className="p-6 text-center">
            <AlertCircle className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <div className="text-slate-900 font-semibold text-sm">No AI recommendations yet</div>
            <div className="text-[12px] text-slate-500 mt-1">
              Run any Intelligence Engine and the AI Recommendation tab will suggest your next best move.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recs.recommended_actions.slice(0, 5).map((a, i) => (
              <li key={i} data-testid={`overview-action-${i}`} className="px-4 py-3 text-[13px] text-slate-800 flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                <span className="flex-1">{a.title || a.action || "Recommended action"}</span>
                {a.priority && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">{a.priority}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Notes */}
      {property.notes && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">Notes</div>
          <div className="text-[13px] text-slate-800 whitespace-pre-wrap">{property.notes}</div>
        </div>
      )}
    </div>
  );
}
